import { ServiceModel } from "../data/entities";

/**
 * Google Tag Manager / GA4 dataLayer helpers for the public booking pages.
 *
 * Events are buffered until the tenant's tracking configuration is known:
 * once a valid container ID arrives the buffer is flushed into the
 * dataLayer (after the gtm.js start event); if the tenant has no container
 * the buffer is dropped and nothing is ever written.
 *
 * Every event pushes the same keys, null when not applicable:
 *   event, booking_id, location_code, skus, invoice_number, amount_due_now
 * plus `ecommerce` on GA4 ecommerce events. booking_id ties together all
 * events for one booking once the draft exists (from booking_initiated on).
 */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

const GTM_ID_PATTERN = /^GTM-[A-Z0-9]{4,12}$/;
const TRACKED_PURCHASES_KEY = "trackedBookingPurchases";

let trackingState: "pending" | "enabled" | "disabled" = "pending";
let pendingEvents: Record<string, unknown>[] = [];
let currentLocationCode: string | null = null;

interface TrackableBookingEvent {
  isAnyProvider?: boolean;
  effectivePrice?: number;
  service?: Partial<ServiceModel>;
  provider?: { name?: string };
}

interface TrackableBooking {
  id: number;
  bookingEvents: TrackableBookingEvent[];
}

interface TrackableInvoice {
  number?: string;
  total: number;
}

export const isValidGoogleTagManagerId = (id: string) => GTM_ID_PATTERN.test(id);

export const normalizeGoogleTagManagerId = (id?: string | null) =>
  id?.trim().toUpperCase() || null;

/**
 * Injects the GTM container for the current tenant. Passing an empty or
 * invalid ID disables tracking and discards buffered events. Only the
 * first valid container is loaded per page session.
 */
export const initGoogleTagManager = (containerId?: string | null) => {
  if (trackingState === "enabled") return;

  const id = normalizeGoogleTagManagerId(containerId);
  if (!id || !isValidGoogleTagManagerId(id)) {
    trackingState = "disabled";
    pendingEvents = [];
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" }, ...pendingEvents);
  pendingEvents = [];
  trackingState = "enabled";
};

/** Location code reported as `location_code` on every event. */
export const setAnalyticsLocation = (locationCode: string) => {
  currentLocationCode = locationCode;
};

const pushDataLayer = (data: Record<string, unknown>) => {
  if (trackingState === "enabled") window.dataLayer!.push(data);
  else if (trackingState === "pending") pendingEvents.push(data);
};

const toItem = (event: TrackableBookingEvent) => ({
  item_id: event.service?.sku,
  item_name: event.service?.name,
  item_category: event.service?.serviceType?.name,
  item_provider: event.isAnyProvider ? "Any provider" : event.provider?.name,
  price: event.effectivePrice,
  quantity: 1,
});

type AnalyticsItem = ReturnType<typeof toItem>;

interface EventData {
  bookingId?: number | null;
  items?: AnalyticsItem[];
  invoice?: TrackableInvoice;
  /** Present for GA4 ecommerce events; merged over currency, value and items. */
  ecommerce?: Record<string, unknown>;
}

/**
 * Pushes one event with the full set of keys. GTM keeps the last value of
 * every key and merges objects/arrays, so `ecommerce` and `skus` are
 * cleared first and every other key is always written, even when null.
 */
const pushEvent = (event: string, { bookingId = null, items = [], invoice, ecommerce }: EventData = {}) => {
  pushDataLayer({ ecommerce: null, skus: null });
  pushDataLayer({
    event,
    booking_id: bookingId,
    location_code: currentLocationCode,
    skus: items.map((item) => item.item_id),
    invoice_number: invoice?.number ?? null,
    amount_due_now: invoice?.total ?? null,
    ...(ecommerce && {
      ecommerce: {
        currency: "CAD",
        value: Math.round(items.reduce((sum, item) => sum + (item.price ?? 0), 0) * 100) / 100,
        items,
        ...ecommerce,
      },
    }),
  });
};

const bookingData = (booking: TrackableBooking, invoice?: TrackableInvoice): EventData => ({
  bookingId: booking.id,
  items: booking.bookingEvents.map(toItem),
  invoice,
});

// Before the draft booking exists (no booking_id yet)

/** Customer opened a service to book it. */
export const trackServiceSelected = (service: ServiceModel) =>
  pushEvent("select_item", { items: [toItem({ service })], ecommerce: {} });

export const trackServiceAdded = (event: TrackableBookingEvent, price?: number) =>
  pushEvent("add_to_cart", { items: [toItem({ ...event, effectivePrice: price })], ecommerce: {} });

export const trackServiceRemoved = (event: TrackableBookingEvent) =>
  pushEvent("remove_from_cart", { items: [toItem(event)], ecommerce: {} });

export const trackBeginCheckout = (events: TrackableBookingEvent[]) =>
  pushEvent("begin_checkout", { items: events.map(toItem), ecommerce: {} });

// Draft booking lifecycle (booking_id only; the booking may not be loaded)

export const trackDraftEvent = (
  event: "resume_draft_booking" | "discard_draft_booking" | "booking_expired",
  bookingId: number | null
) => pushEvent(event, { bookingId });

// With a loaded booking

/** `booking_initiated` (draft created) and `add_payment_info` (payment modal opened). */
export const trackCheckoutStep = (
  event: "booking_initiated" | "add_payment_info",
  booking: TrackableBooking,
  invoice: TrackableInvoice
) => pushEvent(event, { ...bookingData(booking, invoice), ecommerce: {} });

export const trackPaymentFailed = (booking: TrackableBooking, invoice: TrackableInvoice) =>
  pushEvent("payment_failed", bookingData(booking, invoice));

/**
 * The booking conversion. `value` is the full booking value (all services,
 * including balances due at the appointment); `amount_due_now` is what was
 * charged or held at checkout. Reported once per booking per session.
 */
export const trackBookingPurchase = (booking: TrackableBooking, invoice: TrackableInvoice) => {
  const transactionId = String(booking.id);
  try {
    const tracked: string[] = JSON.parse(sessionStorage.getItem(TRACKED_PURCHASES_KEY) ?? "[]");
    if (tracked.includes(transactionId)) return;
    sessionStorage.setItem(TRACKED_PURCHASES_KEY, JSON.stringify([...tracked, transactionId]));
  } catch {}

  pushEvent("purchase", { ...bookingData(booking, invoice), ecommerce: { transaction_id: transactionId } });
};

export const trackBookingCancelled = (booking: TrackableBooking) =>
  pushEvent("booking_cancelled", bookingData(booking));
